import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { formatMoney } from '@/lib/money';
import { AddRateOverrideForm } from '../components/AddRateOverrideForm';
import { serviceUnitLabel } from '../constants';
import { getCategory } from '../api';
import { serviceKeys, useDeleteService, useService } from '../queries';

export function ServiceDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const { data: service, isPending, isError, error } = useService(publicId);
  const del = useDeleteService();

  const category = useQuery({
    queryKey: [...serviceKeys.all, 'category', service?.category],
    queryFn: () => getCategory(service!.category as number),
    enabled: Boolean(service?.category),
  });

  async function handleDelete() {
    if (!service) return;
    await del.mutateAsync(service.public_id);
    navigate('/finance/catalog');
  }

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/finance/catalog')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/finance/catalog')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{service.name}</h1>
          <Badge variant={service.is_active ? 'default' : 'secondary'}>
            {service.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/finance/catalog/${service.public_id}/edit`)}
          >
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {service.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Existing proposal/invoice lines keep their snapshot (ADR-007). This action cannot
                  be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Rate card</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Category">
            {service.category ? (category.data?.name ?? '…') : '—'}
          </Field>
          <Field label="Unit">{serviceUnitLabel(service.unit)}</Field>
          <Field label="Base rate (USD)">{formatMoney(service.base_rate, 'USD') ?? '—'}</Field>
          <Field label="Description" className="sm:col-span-3">
            {service.description || '—'}
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Per-currency overrides</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {service.overrides.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-muted-foreground py-8 text-center">
                      No overrides — non-USD prices convert via FX at quote time.
                    </TableCell>
                  </TableRow>
                ) : (
                  service.overrides.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>{o.currency}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(o.amount, o.currency) ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <AddRateOverrideForm serviceId={service.id} />
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1 break-words whitespace-pre-wrap">{children}</div>
    </div>
  );
}
